/**
 * GameDistribution HTML5 SDK — preroll, interstitials, rewarded ads, lifecycle.
 * Non-GD builds set VITE_WITH_GAMEDISTRIBUTION=false so this module never touches gdsdk.
 * advertisementSettings.autoplay stays false. Boot calls showAd() once after
 * pause/mute listeners are installed; that first call is the preroll.
 */

import { devLog } from "./dev-log.js";

var GD_SDK_READY_TIMEOUT_MS =
  typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITEST
    ? 100
    : 15000;
var GD_SDK_POLL_MS = 50;

var initPromise = null;
var enabled = false;
var prerollRequested = false;
var lifecycleInstalled = false;
var privacyInstalled = false;
var bootStartSettled = false;
/** Allowed until SDK_GDPR_* says the player refused. */
var privacyConsent = {
  trackingAllowed: true,
  targetingAllowed: true,
  thirdPartyAllowed: true,
};
/** @type {ReturnType<typeof setTimeout> | null} */
var bootStartTimeoutId = null;
/** @type {Array<() => void>} */
var bootStartWaiters = [];
/** @type {Array<() => void>} */
var nextGameStartCallbacks = [];
/** @type {{ setAdPaused?: (paused: boolean) => void } | null} */
var pauseSyncGame = null;
/** @type {{ setPlatformMuted?: (muted: boolean) => void } | null} */
var adAudioSyncSoundManager = null;

function isGdBuild() {
  return (
    typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_WITH_GAMEDISTRIBUTION === "true"
  );
}

function getSdk() {
  if (!isGdBuild() || typeof window === "undefined") return null;
  return window.gdsdk || null;
}

function getSiteSdk() {
  if (!isGdBuild() || typeof window === "undefined") return null;
  return window.siteSDK || null;
}

function refreshEnabled() {
  enabled = !!(isGdBuild() && getSdk() && typeof getSdk().showAd === "function");
  return enabled;
}

function waitForSdk() {
  return new Promise(function (resolve) {
    var immediate = getSdk();
    if (immediate && typeof immediate.showAd === "function") {
      resolve(immediate);
      return;
    }

    var settled = false;
    var timeoutId = setTimeout(function () {
      finish(null);
    }, GD_SDK_READY_TIMEOUT_MS);
    var intervalId = setInterval(function () {
      var sdk = getSdk();
      if (sdk && typeof sdk.showAd === "function") {
        finish(sdk);
      }
    }, GD_SDK_POLL_MS);

    function finish(result) {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      clearInterval(intervalId);
      resolve(result);
    }
  });
}

function muteGameAudio(soundManager) {
  if (!soundManager || typeof soundManager.setPlatformMuted !== "function") return;
  soundManager.setPlatformMuted(true);
}

function restoreGameAudio(soundManager) {
  if (!soundManager || typeof soundManager.setPlatformMuted !== "function") return;
  soundManager.setPlatformMuted(false);
}

function withAdPause(game, startFn) {
  if (game && typeof game.setAdPaused === "function") {
    game.setAdPaused(true);
  }
  if (typeof startFn === "function") startFn();
}

function clearAdPause(game) {
  if (game && typeof game.setAdPaused === "function") {
    game.setAdPaused(false);
  }
}

function clearBootStartTimeout() {
  if (bootStartTimeoutId == null) return;
  clearTimeout(bootStartTimeoutId);
  bootStartTimeoutId = null;
}

function settleBootStart() {
  clearBootStartTimeout();
  if (bootStartSettled) return;
  bootStartSettled = true;
  var waiters = bootStartWaiters.slice();
  bootStartWaiters = [];
  waiters.forEach(function (fn) {
    try {
      fn();
    } catch (e) {
      console.warn("[GdBridge] boot start waiter failed:", e);
    }
  });
}

function notifyGameStart() {
  settleBootStart();
  if (!nextGameStartCallbacks.length) return;
  var callbacks = nextGameStartCallbacks.slice();
  nextGameStartCallbacks = [];
  callbacks.forEach(function (fn) {
    try {
      fn();
    } catch (e) {
      console.warn("[GdBridge] runAfterNextGameStart callback failed:", e);
    }
  });
}

/** Unpause and continue gameplay after an ad ends, fails, or is skipped. */
function resumeAfterPortalAd(game) {
  clearAdPause(game || pauseSyncGame);
  restoreGameAudio(adAudioSyncSoundManager);
  notifyGameStart();
}

function installLifecycleListenersIfNeeded() {
  if (lifecycleInstalled) return;
  var siteSdk = getSiteSdk();
  if (!siteSdk || typeof siteSdk.initLifecycleListeners !== "function") return;
  lifecycleInstalled = true;
  siteSdk.initLifecycleListeners(
    function () {
      if (pauseSyncGame && typeof pauseSyncGame.setAdPaused === "function") {
        pauseSyncGame.setAdPaused(true);
      }
      muteGameAudio(adAudioSyncSoundManager);
    },
    function () {
      resumeAfterPortalAd(pauseSyncGame);
    },
    { replayStart: false }
  );
}

function installPrivacyListener() {
  if (privacyInstalled) return;
  var siteSdk = getSiteSdk();
  if (!siteSdk || typeof siteSdk.onPrivacyConsent !== "function") return;
  privacyInstalled = true;
  siteSdk.onPrivacyConsent(function (consent) {
    if (!consent) return;
    privacyConsent = {
      trackingAllowed: consent.trackingAllowed !== false,
      targetingAllowed: consent.targetingAllowed !== false,
      thirdPartyAllowed: consent.thirdPartyAllowed !== false,
    };
  });
}

/** One non-rewarded showAd() before the first run. Settles boot on end or failure. */
function kickOffPrerollAd(game) {
  function finish(err) {
    if (err) console.warn("[GdBridge] preroll failed:", err);
    resumeAfterPortalAd(game);
  }

  var siteSdk = getSiteSdk();
  if (siteSdk && typeof siteSdk.displayPrerollAd === "function") {
    siteSdk.displayPrerollAd(
      function () {
        withAdPause(game);
        muteGameAudio(adAudioSyncSoundManager);
      },
      function () {
        finish();
      },
      function (err) {
        finish(err);
      }
    );
    return;
  }

  var sdk = getSdk();
  if (!sdk || typeof sdk.showAd !== "function") {
    finish();
    return;
  }
  withAdPause(game);
  muteGameAudio(adAudioSyncSoundManager);
  sdk
    .showAd()
    .then(function () {
      finish();
    })
    .catch(function (err) {
      finish(err);
    });
}

function beginBootWait(timeoutMs) {
  if (!enabled) return Promise.resolve();
  installLifecycleListenersIfNeeded();
  installPrivacyListener();
  var siteSdk = getSiteSdk();
  var adActive = !!(
    siteSdk &&
    typeof siteSdk.isAdBreakActive === "function" &&
    siteSdk.isAdBreakActive()
  );
  if (!prerollRequested && !adActive) {
    prerollRequested = true;
    kickOffPrerollAd(pauseSyncGame);
  }
  if (bootStartSettled) return Promise.resolve();
  return new Promise(function (resolve) {
    bootStartWaiters.push(resolve);
    clearBootStartTimeout();
    bootStartTimeoutId = setTimeout(function () {
      bootStartTimeoutId = null;
      console.warn("[GdBridge] waitForInitialGameStart timed out");
      settleBootStart();
    }, timeoutMs);
  });
}

function kickOffInterstitialAd(game) {
  var siteSdk = getSiteSdk();
  if (siteSdk && typeof siteSdk.displayAd === "function") {
    siteSdk.displayAd(
      function () {
        withAdPause(game);
      },
      function () {
        resumeAfterPortalAd(game);
      },
      function (err) {
        console.warn("[GdBridge] interstitial failed:", err);
        resumeAfterPortalAd(game);
      }
    );
    return;
  }

  var sdk = getSdk();
  if (!sdk || typeof sdk.showAd !== "function") {
    resumeAfterPortalAd(game);
    return;
  }
  withAdPause(game);
  sdk
    .showAd()
    .then(function () {
      resumeAfterPortalAd(game);
    })
    .catch(function (err) {
      console.warn("[GdBridge] interstitial failed:", err);
      resumeAfterPortalAd(game);
    });
}

export const GdBridge = {
  ensureInitialized: function () {
    if (initPromise) return initPromise;
    if (!isGdBuild()) {
      enabled = false;
      initPromise = Promise.resolve();
      return initPromise;
    }
    devLog("[GdBridge] waiting for gdsdk");
    initPromise = waitForSdk()
      .then(function () {
        refreshEnabled();
        installPrivacyListener();
        if (!enabled) {
          console.warn("[GdBridge] gdsdk not available — continuing without portal ads");
        }
      })
      .catch(function (err) {
        enabled = false;
        console.warn("[GdBridge] init failed:", err);
      });
    return initPromise;
  },

  isEnabled: function () {
    return !!enabled;
  },

  canOfferRewardedAds: function () {
    return !!enabled;
  },

  /**
   * Request the boot preroll, then wait until it ends, fails, or times out.
   * The SDK-ready `SDK_GAME_START` is not replayed (`replayStart: false`), so it
   * cannot skip the preroll. A later start, or showAd settling, continues boot.
   * @param {{ timeoutMs?: number }} [options]
   * @returns {Promise<void>}
   */
  waitForInitialGameStart: function (options) {
    var opts = options || {};
    var emulatorBoot =
      typeof window !== "undefined" && !!window.__GD_EMULATOR_SDK__;
    var defaultTimeout = emulatorBoot ? 3000 : GD_SDK_READY_TIMEOUT_MS;
    var timeoutMs = opts.timeoutMs != null ? opts.timeoutMs : defaultTimeout;
    if (enabled) return Promise.resolve(beginBootWait(timeoutMs));
    return GdBridge.ensureInitialized().then(function () {
      return beginBootWait(timeoutMs);
    });
  },

  /**
   * False after SDK_GDPR_TRACKING or SDK_GDPR_THIRD_PARTY. Targeting opt-out
   * does not hide the board; this game has no second ad network.
   * @returns {boolean}
   */
  canUseLeaderboard: function () {
    return !!(privacyConsent.trackingAllowed && privacyConsent.thirdPartyAllowed);
  },

  /**
   * Run once when the next ad break finishes, fails, or is skipped.
   * @param {() => void} callback
   */
  runAfterNextGameStart: function (callback) {
    if (typeof callback !== "function") return;
    nextGameStartCallbacks.push(callback);
  },

  /**
   * Kick off a mid-session interstitial. Resolves when the request is dispatched, not when the ad ends.
   * Pair with {@link runAfterNextGameStart} to wait for settle or `SDK_GAME_START`.
   * @param {{ game?: { setAdPaused?: (paused: boolean) => void } }} [options]
   * @returns {Promise<void>}
   */
  requestInterstitialAd: function (options) {
    var opts = options || {};
    var game = opts.game;
    return GdBridge.ensureInitialized().then(function () {
      if (!enabled) return;
      installLifecycleListenersIfNeeded();
      kickOffInterstitialAd(game);
    });
  },

  /**
   * @param {{
   *   name?: string,
   *   onStarted?: () => void,
   *   onFinished?: () => void,
   *   onError?: (err: unknown) => void,
   *   game?: { setAdPaused?: (paused: boolean) => void }
   * }} [options]
   * @returns {Promise<{ granted: boolean, cancelled: boolean }>}
   */
  requestRewardedAd: function (options) {
    var opts = options || {};
    var started = typeof opts.onStarted === "function" ? opts.onStarted : null;
    var finished = typeof opts.onFinished === "function" ? opts.onFinished : null;
    var onError = typeof opts.onError === "function" ? opts.onError : null;
    var game = opts.game;

    return GdBridge.ensureInitialized().then(function () {
      if (!enabled) {
        if (onError) onError(new Error("GameDistribution rewarded ad unavailable"));
        return { granted: false, cancelled: false };
      }

      var siteSdk = getSiteSdk();
      if (siteSdk && typeof siteSdk.displayRewardedAd === "function") {
        return new Promise(function (resolve) {
          siteSdk.displayRewardedAd(
            function () {
              withAdPause(game, started);
            },
            function () {
              clearAdPause(game);
              if (finished) finished();
              resolve({ granted: true, cancelled: false });
            },
            function (err) {
              console.warn("[GdBridge] rewarded ad failed:", err);
              if (onError) onError(err);
              clearAdPause(game);
              resolve({ granted: false, cancelled: true });
            }
          );
        });
      }

      if (onError) onError(new Error("GameDistribution rewarded ad unavailable"));
      return { granted: false, cancelled: false };
    });
  },

  shouldShowPlayAgainInterstitial: function (game) {
    return !!(enabled && game);
  },

  /**
   * @param {{ setAdPaused?: (paused: boolean) => void }} game
   */
  installPauseSync: function (game) {
    if (!enabled || !game || typeof game.setAdPaused !== "function") return;
    pauseSyncGame = game;
    installLifecycleListenersIfNeeded();
  },

  /**
   * @param {{ setPlatformMuted?: (muted: boolean) => void }} soundManager
   */
  installAdAudioSync: function (soundManager) {
    if (!enabled || !soundManager || typeof soundManager.setPlatformMuted !== "function") return;
    adAudioSyncSoundManager = soundManager;
    installLifecycleListenersIfNeeded();
  },
};

/** Test helper — reset singleton between tests. */
export function resetGdBridgeForTests() {
  clearBootStartTimeout();
  initPromise = null;
  enabled = false;
  prerollRequested = false;
  lifecycleInstalled = false;
  privacyInstalled = false;
  privacyConsent = {
    trackingAllowed: true,
    targetingAllowed: true,
    thirdPartyAllowed: true,
  };
  bootStartSettled = false;
  bootStartWaiters = [];
  nextGameStartCallbacks = [];
  pauseSyncGame = null;
  adAudioSyncSoundManager = null;
}
